import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { uploadToCloudinary } from '../../lib/cloudinary'
import { 
  User, 
  Phone, 
  Mail, 
  MessageCircle, 
  MapPin, 
  FileText,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Plus,
  X,
  Upload,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react'

export default function EditVCard() {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    title: '',
    phone: '',
    email: '',
    whatsapp: '',
    about: '',
    address: '',
    profileImage: '',
    social: {
      instagram: '',
      facebook: '',
      linkedin: '',
      youtube: ''
    },
    services: [],
    gallery: [],
    theme: 'default'
  })

  const [newService, setNewService] = useState('')

  useEffect(() => {
    async function fetchVCard() {
      if (!currentUser) return

      try {
        const vcardDoc = await getDoc(doc(db, 'vcards', currentUser.uid))
        if (vcardDoc.exists()) {
          setFormData(prev => ({ ...prev, ...vcardDoc.data() }))
        } else {
          // Set default username from email
          const defaultUsername = currentUser.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '')
          setFormData(prev => ({ 
            ...prev, 
            username: defaultUsername,
            email: currentUser.email,
            name: currentUser.displayName || ''
          }))
        }
      } catch (error) {
        console.error('Error fetching vCard:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchVCard()
  }, [currentUser])

  function handleChange(e) {
    const { name, value } = e.target
    if (name.startsWith('social.')) {
      const socialField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        social: { ...prev.social, [socialField]: value }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' })
      return
    }

    setUploadingImage(true)
    try {
      const url = await uploadToCloudinary(file)
      setFormData(prev => ({ ...prev, profileImage: url }))
      setMessage({ type: 'success', text: 'Image uploaded successfully' })
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload image' })
    } finally {
      setUploadingImage(false)
    }
  }

  function addService() {
    if (!newService.trim()) return
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, newService.trim()]
    }))
    setNewService('')
  }

  function removeService(index) {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!currentUser) return

    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      await setDoc(doc(db, 'vcards', currentUser.uid), {
        ...formData,
        uid: currentUser.uid,
        updatedAt: serverTimestamp(),
        createdAt: formData.createdAt || serverTimestamp()
      })

      // Create analytics document if it doesn't exist
      const analyticsRef = doc(db, 'analytics', currentUser.uid)
      const analyticsDoc = await getDoc(analyticsRef)
      if (!analyticsDoc.exists()) {
        await setDoc(analyticsRef, {
          username: formData.username,
          visits: 0,
          qrScans: 0,
          lastVisited: null
        })
      }

      setMessage({ type: 'success', text: 'vCard saved successfully!' })
    } catch (error) {
      console.error('Error saving vCard:', error)
      setMessage({ type: 'error', text: 'Failed to save vCard. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '50vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="edit-vcard-page">
      <div className="page-header">
        <h1 className="page-title">Edit Your vCard</h1>
        <p className="page-subtitle">Update your digital business card details</p>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 'var(--space-lg)' }}>
          {message.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="edit-layout">
        <form onSubmit={handleSubmit} className="edit-form dashboard-card">
          {/* Profile Image */}
          <div className="form-section">
            <h3 className="form-section-title">Profile Photo</h3>
            <div className="profile-upload">
              <div 
                className="upload-preview"
                style={{ 
                  backgroundImage: formData.profileImage ? `url(${formData.profileImage})` : 'none'
                }}
              >
                {!formData.profileImage && <User size={40} />}
              </div>
              <div className="upload-actions">
                <label className="btn btn-secondary btn-sm">
                  <Upload size={16} />
                  {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                  />
                </label>
                <p className="text-xs text-muted">Max 5MB, JPG or PNG</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <h3 className="form-section-title">Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  placeholder="yourname"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
                <span className="form-hint">Your vCard URL: /v/{formData.username || 'yourname'}</span>
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Job Title / Profession</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  placeholder="Business Consultant"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="form-section">
            <h3 className="form-section-title">Contact Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label"><Phone size={14} /> Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><MessageCircle size={14} /> WhatsApp</label>
                <input
                  type="tel"
                  name="whatsapp"
                  className="form-input"
                  placeholder="+91 98765 43210"
                  value={formData.whatsapp}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><Mail size={14} /> Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><MapPin size={14} /> Address</label>
                <input
                  type="text"
                  name="address"
                  className="form-input"
                  placeholder="Mumbai, India"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* About */}
          <div className="form-section">
            <h3 className="form-section-title">About</h3>
            <div className="form-group">
              <label className="form-label"><FileText size={14} /> Bio</label>
              <textarea
                name="about"
                className="form-input form-textarea"
                placeholder="Tell people about yourself or your business..."
                value={formData.about}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="form-section">
            <h3 className="form-section-title">Social Links</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label"><Instagram size={14} /> Instagram</label>
                <input
                  type="text"
                  name="social.instagram"
                  className="form-input"
                  placeholder="@username"
                  value={formData.social.instagram}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><Facebook size={14} /> Facebook</label>
                <input
                  type="text"
                  name="social.facebook"
                  className="form-input"
                  placeholder="facebook.com/username"
                  value={formData.social.facebook}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><Linkedin size={14} /> LinkedIn</label>
                <input
                  type="text"
                  name="social.linkedin"
                  className="form-input"
                  placeholder="linkedin.com/in/username"
                  value={formData.social.linkedin}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><Youtube size={14} /> YouTube</label>
                <input
                  type="text"
                  name="social.youtube"
                  className="form-input"
                  placeholder="youtube.com/@channel"
                  value={formData.social.youtube}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="form-section">
            <h3 className="form-section-title">Services</h3>
            <div className="services-list">
              {formData.services.map((service, index) => (
                <div key={index} className="service-tag">
                  <span>{service}</span>
                  <button type="button" onClick={() => removeService(index)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="add-service">
              <input
                type="text"
                className="form-input"
                placeholder="Add a service..."
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
              />
              <button type="button" className="btn btn-secondary" onClick={addService}>
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Submit */}
          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={saving}>
            {saving ? <span className="spinner"></span> : <><Save size={20} /> Save Changes</>}
          </button>
        </form>

        {/* Live Preview */}
        <div className="preview-section">
          <div className="preview-sticky">
            <h3 className="preview-title">Live Preview</h3>
            <div className="vcard-preview-card">
              <div className="vcard-preview-header">
                <div 
                  className="vcard-preview-avatar"
                  style={{ 
                    backgroundImage: formData.profileImage ? `url(${formData.profileImage})` : 'none'
                  }}
                >
                  {!formData.profileImage && (formData.name?.[0] || 'U')}
                </div>
                <h4>{formData.name || 'Your Name'}</h4>
                <p>{formData.title || 'Your Title'}</p>
              </div>
              <div className="vcard-preview-buttons">
                {formData.phone && <button>📞 Call</button>}
                {formData.whatsapp && <button>💬 WhatsApp</button>}
                {formData.email && <button>✉️ Email</button>}
              </div>
              {formData.about && (
                <div className="vcard-preview-section">
                  <span>About</span>
                  <p>{formData.about}</p>
                </div>
              )}
              {formData.services.length > 0 && (
                <div className="vcard-preview-section">
                  <span>Services</span>
                  <ul>
                    {formData.services.slice(0, 3).map((s, i) => (
                      <li key={i}>• {s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .edit-layout {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: var(--space-lg);
          align-items: start;
        }
        
        @media (max-width: 1100px) {
          .edit-layout {
            grid-template-columns: 1fr;
          }
          .preview-section {
            display: none;
          }
        }
        
        .form-section {
          margin-bottom: var(--space-xl);
          padding-bottom: var(--space-xl);
          border-bottom: 1px solid var(--gray-200);
        }
        
        .form-section:last-of-type {
          border-bottom: none;
        }
        
        .form-section-title {
          font-size: var(--font-size-base);
          font-weight: 600;
          margin-bottom: var(--space-md);
          color: var(--text-dark);
        }
        
        .form-label svg {
          margin-right: 4px;
          vertical-align: middle;
        }
        
        .form-hint {
          display: block;
          margin-top: 4px;
          font-size: var(--font-size-xs);
          color: var(--text-muted);
        }
        
        .profile-upload {
          display: flex;
          align-items: center;
          gap: var(--space-lg);
        }
        
        .upload-preview {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: var(--gray-100);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gray-400);
          background-size: cover;
          background-position: center;
        }
        
        .services-list {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }
        
        .service-tag {
          display: inline-flex;
          align-items: center;
          gap: var(--space-sm);
          padding: 6px 12px;
          background: var(--gray-100);
          border-radius: var(--radius-full);
          font-size: var(--font-size-sm);
        }
        
        .service-tag button {
          background: none;
          color: var(--gray-500);
          padding: 0;
          line-height: 1;
        }
        
        .service-tag button:hover {
          color: var(--error);
        }
        
        .add-service {
          display: flex;
          gap: var(--space-sm);
        }
        
        .add-service input {
          flex: 1;
        }
        
        .preview-sticky {
          position: sticky;
          top: var(--space-lg);
        }
        
        .preview-title {
          font-size: var(--font-size-sm);
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: var(--space-md);
        }
        
        .vcard-preview-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-lg);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--gray-200);
        }
        
        .vcard-preview-header {
          text-align: center;
          margin-bottom: var(--space-md);
        }
        
        .vcard-preview-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--font-size-2xl);
          font-weight: 600;
          margin: 0 auto var(--space-md);
          background-size: cover;
          background-position: center;
        }
        
        .vcard-preview-header h4 {
          font-size: var(--font-size-lg);
          font-weight: 600;
          margin-bottom: 2px;
        }
        
        .vcard-preview-header p {
          font-size: var(--font-size-sm);
          color: var(--text-muted);
        }
        
        .vcard-preview-buttons {
          display: flex;
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }
        
        .vcard-preview-buttons button {
          flex: 1;
          padding: 10px;
          background: var(--gray-100);
          border-radius: var(--radius-md);
          font-size: var(--font-size-xs);
        }
        
        .vcard-preview-section {
          padding-top: var(--space-md);
          border-top: 1px solid var(--gray-200);
        }
        
        .vcard-preview-section span {
          font-size: var(--font-size-xs);
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .vcard-preview-section p,
        .vcard-preview-section li {
          font-size: var(--font-size-sm);
          color: var(--text-body);
          margin-top: var(--space-sm);
        }
        
        .vcard-preview-section ul {
          margin-top: var(--space-sm);
        }
      `}</style>
    </div>
  )
}
